import type { ReactNode } from "react";
import { useId } from "react";
import {
	DialogTrigger as AriaDialogTrigger,
	Heading as AriaHeading,
} from "react-aria-components";
import {
	Dialog,
	Modal,
	ModalOverlay,
} from "@/components/application/modals/modal";
import { Avatar } from "@/components/base/avatar/avatar";
import { Button } from "@/components/base/buttons/button";

export interface ConfirmationModalProps {
	/** Whether the modal is open */
	isOpen: boolean;
	/** Callback to change open state */
	setIsOpen: (open: boolean) => void;
	/** Modal title */
	title: string;
	/** Modal description/message */
	description: ReactNode;
	/** Icon to display at the top of the modal */
	icon?: ({ className }: { className?: string }) => ReactNode;
	/** Icon background color (Tailwind class) */
	iconBgColor?: string;
	/** Icon stroke color (CSS variable) */
	iconStrokeColor?: string;
	/** Primary button text */
	confirmText?: string;
	/** Cancel button text */
	cancelText?: string;
	/** Callback when confirm button is clicked */
	onConfirm: () => void | Promise<void>;
	/** Callback when cancel button is clicked */
	onCancel?: () => void;
	/** Whether the action is in progress */
	isLoading?: boolean;
	/** Whether the modal can be dismissed by clicking outside */
	isDismissable?: boolean;
	confirmTestId?: string;
	/** Primary button color variant */
	confirmColor?:
		| "primary"
		| "secondary"
		| "tertiary"
		| "link-gray"
		| "link-color"
		| "primary-destructive"
		| "secondary-destructive"
		| "tertiary-destructive"
		| "link-destructive"
		| undefined;
}

export const ConfirmationModal = ({
	isOpen,
	setIsOpen,
	title,
	description,
	icon,
	confirmText = "Confirm",
	cancelText = "Cancel",
	onConfirm,
	onCancel,
	isLoading = false,
	isDismissable = false,
	confirmTestId,
	confirmColor = "primary",
	iconBgColor = "!bg-[#F4EBFF]",
}: ConfirmationModalProps) => {
	const titleId = useId();
	const descriptionId = useId();

	const handleConfirm = async () => {
		await onConfirm();
	};

	const handleCancel = () => {
		if (onCancel) {
			onCancel();
		}
		setIsOpen(false);
	};

	return (
		<AriaDialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
			<ModalOverlay isDismissable={isDismissable}>
				<Modal>
					<Dialog>
						<div className="relative w-full overflow-hidden rounded-2xl bg-primary shadow-xl sm:max-w-100">
							<div className="flex flex-col gap-4 px-4 pt-5 sm:px-6 sm:pt-6">
								<div className="relative w-max mx-auto">
									<Avatar
										size="2xl"
										placeholderIcon={icon}
										className={iconBgColor}
									/>
								</div>
								<div className="z-10 flex flex-col gap-0.5 text-center">
									<AriaHeading
										id={titleId}
										slot="title"
										className="text-md font-semibold text-primary"
									>
										{title}
									</AriaHeading>
									<div id={descriptionId} className="text-sm text-tertiary">
										{description}
									</div>
								</div>
							</div>
							<div className="pt-2 sm:pt-8">
								<div className="z-10 flex flex-1 flex-col-reverse gap-3 border-t border-secondary p-4 *:grow sm:grid sm:grid-cols-2 sm:p-6">
									<Button
										color="secondary"
										size="lg"
										onClick={handleCancel}
										isDisabled={isLoading}
									>
										{cancelText}
									</Button>
									<Button
										color={confirmColor}
										size="lg"
										onClick={handleConfirm}
										isLoading={isLoading}
										isDisabled={isLoading}
										showTextWhileLoading
										data-testid={confirmTestId}
									>
										{confirmText}
									</Button>
								</div>
							</div>
						</div>
					</Dialog>
				</Modal>
			</ModalOverlay>
		</AriaDialogTrigger>
	);
};
